import { gql } from '@apollo/client';
import { CHAT_ROOM_NOTE_FIELDS, FORWARDED_SOURCE_FIELDS } from './cursor.graphql';
import { AI_AGENT_FIELDS, CHAT_MESSAGE_SCHEDULE_FIELDS } from './query.graphql';

export const MUTATION_SIGN_OUT = gql`
  mutation signOut($input: InputRefreshToken) {
    signOut(input: $input) {
      message
    }
  }
`;

export const MUTATION_SIGN_UP = gql`
  mutation SignUp($input: InputAuthSignUp!) {
    signUp(input: $input) {
      message
      data {
        email
        isEmailVerified
        isPhoneNumberVerified
      }
    }
  }
`;

export const MUTATION_SIGN_UP_V2 = gql`
  mutation SignUpV2($input: InputAuthSignUpV2!) {
    signUpV2(input: $input) {
      message
      data {
        email
        isEmailVerified
        isPhoneNumberVerified
      }
    }
  }
`;

export const MUTATION_CONFIRM_SIGNUP_AND_LOGIN = gql`
  mutation ConfirmSignupAndLogin($input: InputAuthConfirmSignUp) {
    confirmSignupAndLogin(input: $input) {
      message
      data {
        token {
          sessionId
          refreshTokenIssuedAt
          refreshTokenExpiresAt
          refresh
          accessTokenIssuedAt
          accessTokenExpiresAt
          access
        }
        isEmailVerified
        twoFactorAuthenticationRequired
      }
    }
  }
`;

export const MUTATION_FORGOT_PASSWORD = gql`
  mutation ForgotPassword($input: InputForgotPassword) {
    forgotPassword(input: $input) {
      message
      data {
        email
      }
    }
  }
`;

export const MUTATION_LOGIN = gql`
  mutation Login($input: InputAuthLogin!) {
    login(input: $input) {
      message
      data {
        token {
          sessionId
          access
          accessTokenIssuedAt
          accessTokenExpiresAt
          refresh
          refreshTokenIssuedAt
          refreshTokenExpiresAt
        }
        isEmailVerified
        twoFactorAuthenticationRequired
        challengeName
        name
        email
        id
      }
    }
  }
`;

export const CREATE_TEAMS = gql`
  mutation CreateTeams($input: createTeamsInput!) {
    createTeams(input: $input) {
      message
      data {
        totalMembers
        name
        status
        workspacesId
        metrics {
          key
          value
        }
        createdBy
        updatedBy
        description
        id
        members {
          id
          name
          profilePicture {
            id
            url
          }
          accountStatus
        }
      }
    }
  }
`;

export const UPDATE_TEAMS = gql`
  mutation UpdateTeams($updateTeamsId: String!, $input: updateTeamsInput!) {
    updateTeams(id: $updateTeamsId, input: $input) {
      message
      data {
        id
        name
        description
        status
        teamMembers {
          userId
          workspacesId
          teamId
          status
          user {
            id
            name
            email
            phoneNumber
            profilePicture {
              id
              url
              originalname
            }
            address
          }
        }
      }
    }
  }
`;

export const DELETE_TEAMS = gql`
  mutation DeleteTeams($deleteTeamsId: String!) {
    deleteTeams(id: $deleteTeamsId) {
      message
    }
  }
`;

export const ADD_TEAM_MEMBER = gql`
  mutation AddTeamMember($input: [InputTeamMembersInterface]!) {
    addTeamMember(input: $input) {
      message
    }
  }
`;

export const DELETE_TEAM_MEMBER = gql`
  mutation DeleteTeamMember($deleteTeamMemberId: Int!) {
    deleteTeamMember(id: $deleteTeamMemberId) {
      message
    }
  }
`;

export const MUTATION_BLOCK_OR_UNBLOCK_CONTACT = gql`
  mutation BlockOrUnblockContact($blockOrUnblockContactId: Int, $input: InputBlockOrUnblockContact) {
    blockOrUnblockContact(id: $blockOrUnblockContactId, input: $input) {
      data {
        id
        isBlocked
        isPublished
        phoneNumber
        name
        blockedOn
        timeZone
        blockedBy {
          name
        }
        blockReason
        country {
          id
          flag
        }
        profilePicture {
          id
          url
        }
      }
      message
    }
  }
`;

export const MUTATION_CONFIRM_FORGOT_PASSWORD = gql`
  mutation ConfirmForgotPassword($input: InputConfirmForgotPassword) {
    confirmForgotPassword(input: $input) {
      message
    }
  }
`;

export const SEND_MESSAGE = gql`
  mutation SendMessage($input: InputSendMessage) {
    sendMessage(input: $input) {
      message
      data {
        id
        markAsClosed
        direction
        durationConference {
          endDate
          startDate
        }
        client {
          id
          name
          phoneNumber
          profilePicture {
            id
            url
          }
          country {
            id
            flag
          }
        }
        user {
          name
          isAiAgent
        }
        agent {
          callPlatform
          user {
            id
            name
            profilePicture {
              id
              url
            }
          }
        }
        pinned
        channelId
        conversationStatus
        messageContent
        conversationType
        recordingUrl
        numMedia
        friendlyName
        voicemailRecordingUrl
        createdAt
        forwardedAt
        forwardedTo {
          id
          externalNumber
          channel {
            id
            friendlyName
            phoneNumber
          }
        }
      }
    }
  }
`;

export const PIN_UNPIN_MESSAGE_CONVERSATION = gql`
  mutation PinOrUnpinMessageConversation(
    $pinOrUnpinMessageConversationId: Int!
    $input: InputPinUnpinMessageConversation
  ) {
    pinOrUnpinMessageConversation(id: $pinOrUnpinMessageConversationId, input: $input) {
      message
      data {
        id
        pinned
      }
    }
  }
`;

export const MUTATION_TWO_FACTOR_AUTHENTICATION = gql`
  mutation VerifyTwoFactorAuthentication($input: InputVerifyTwoFactorAuthentication) {
    verifyTwoFactorAuthentication(input: $input) {
      message
      data {
        token {
          sessionId
          access
          refresh
        }
        isEmailVerified
        twoFactorAuthenticationRequired
      }
    }
  }
`;

export const MUTATION_RESEND_TWO_FACTOR_AUTHENTICATION = gql`
  mutation ResendTwoFactorAuthenticationCode($email: String) {
    resendTwoFactorAuthenticationCode(email: $email) {
      message
    }
  }
`;

export const MUTATION_WORKSPACE_LOGIN = gql`
  mutation WorkspaceLogin($input: InputWorkspaceLogin) {
    workspaceLogin(input: $input) {
      data {
        token {
          access
        }

        workspaceUser {
          id
          workspace {
            status
            hasCompleted
            label
            logo {
              url
            }
          }
        }
      }
      message
    }
  }
`;

export const CREATE_COMMAND_RESPONSE = gql`
  mutation CreateCommandResponse($input: CommandResponseInput) {
    createCommandResponse(input: $input) {
      data {
        title
        id
        content
        command
      }
      message
    }
  }
`;

export const UPDATE_COMMAND_RESPONSE = gql`
  mutation UpdateCommandResponse($updateCommandResponseId: Int!, $input: CommandResponseInput) {
    updateCommandResponse(id: $updateCommandResponseId, input: $input) {
      message
      data {
        id
        title
        command
        content
      }
    }
  }
`;

export const DELETE_COMMAND_RESPONSE = gql`
  mutation DeleteCommandResponse($deleteCommandResponseId: Int!) {
    deleteCommandResponse(id: $deleteCommandResponseId) {
      message
      data {
        id
        title
        command
        content
      }
    }
  }
`;

export const MUTATION_PRECHECK_LOGIN = gql`
  mutation PreCheckLogin($input: PreCheckLogin) {
    preCheckLogin(input: $input) {
      message
      data {
        platformExists
      }
    }
  }
`;

export const CREATE_CUSTOMER_V2 = gql`
  mutation CreateCustomerV2($input: InputCreateCustomerV2) {
    createCustomerV2(input: $input) {
      message
    }
  }
`;

export const CREATE_INTENT_V2 = gql`
  mutation CreatePaymentIntentV2($amount: Int, $paymentGateway: PaymentGatewayEnum) {
    createPaymentIntentV2(amount: $amount, paymentGateway: $paymentGateway) {
      message
      data
    }
  }
`;

export const CREATE_SUBSCRIPTION_V2 = gql`
  mutation CreateSubscription($input: InputCreateSubscription) {
    createSubscription(input: $input) {
      message
    }
  }
`;

export const RESEND_CONFIRMATION_CODE = gql`
  mutation ResendConfirmationCode($input: InputAuthResendConfirmationCode!) {
    resendConfirmationCode(input: $input) {
      message
    }
  }
`;

export const CREATE_FCM_TOKEN = gql`
  mutation CreateUserWorkspaceFcmToken($input: InputUserWorkspaceFCMToken!) {
    createUserWorkspaceFcmToken(input: $input) {
      message
      data {
        id
        token
        platform
      }
    }
  }
`;

export const DELETE_WORKSPACE_FCM_TOKEN = gql`
  mutation DeleteUserWorkspaceFcmToken {
    deleteUserWorkspaceFcmToken {
      message
    }
  }
`;

export const CREATE_PROVIDER_ERROR_LOG = gql`
  mutation CreateProviderErrorLog($input: InputProviderErrorLogData) {
    createProviderErrorLog(input: $input) {
      message
    }
  }
`;

export const CREATE_CONTACT_NOTE = gql`
  mutation CreateContactNote($input: InputContactNote!) {
    createContactNote(input: $input) {
      message
      data {
        id
        description
        createdAt
        updatedAt
        updatedBy {
          id
          user {
            id
            name
            profilePicture {
              id
              url
            }
          }
        }
      }
    }
  }
`;

export const UPDATE_CONTACT_NOTE = gql`
  mutation UpdateContactNote($updateContactNoteId: Int!, $input: InputContactNote!) {
    updateContactNote(id: $updateContactNoteId, input: $input) {
      message
      data {
        id
        description
        createdAt
        updatedAt
        updatedBy {
          id
          user {
            id
            name
            profilePicture {
              id
              url
            }
          }
        }
      }
    }
  }
`;

export const DELETE_CONTACT_NOTE = gql`
  mutation DeleteContactNote($deleteContactNoteId: Int!) {
    deleteContactNote(id: $deleteContactNoteId) {
      message
      data {
        id
      }
    }
  }
`;

export const CREATE_TAG = gql`
  mutation CreateTag($input: InputTag!) {
    createTag(input: $input) {
      message
      data {
        id
        label
        color
      }
    }
  }
`;

export const UPDATE_TAG = gql`
  mutation UpdateTag($updateTagId: Int!, $input: InputTag!) {
    updateTag(id: $updateTagId, input: $input) {
      message
      data {
        id
        label
        slug
        type
        color
      }
    }
  }
`;

export const DELETE_TAGS = gql`
  mutation DeleteTags($ids: [Int!]!) {
    deleteTags(ids: $ids) {
      message
      data {
        id
      }
    }
  }
`;

export const ASSIGN_TAGS_TO_CONTACT = gql`
  mutation AssignTagsToContact($input: AssignUnassignContactTag) {
    assignTagsToContact(input: $input) {
      message
      data {
        id
        contact {
          id
        }
        tags {
          type
          slug
          label
          id
          color
        }
      }
    }
  }
`;

export const UNASSIGN_TAGS_FROM_CONTACT = gql`
  mutation UnassignTagsFromContact($input: AssignUnassignContactTag) {
    unassignTagsFromContact(input: $input) {
      message
      data {
        tags {
          id
          label
          color
        }
      }
    }
  }
`;

export const MUTATION_SET_DEFAULT_CHANNEL = gql`
  mutation SetDefaultChannel($channelId: Int!) {
    setDefaultChannel(channelId: $channelId) {
      message
      data {
        id
      }
    }
  }
`;

export const MUTATION_SET_DEFAULT_COUNTRY = gql`
  mutation SetDefaultCountry($input: InputSetDefaultCountry) {
    setDefaultCountry(input: $input) {
      message
    }
  }
`;

export const MUTATION_UPDATE_CONTACT = gql`
  mutation UpdateContact($updateContactId: Int!, $input: InputContact!) {
    updateContact(id: $updateContactId, input: $input) {
      message
      data {
        id
        email
        name
        companyName
        website
        address
        profilePicture {
          id
          url
        }
      }
    }
  }
`;

export const CREATE_OR_UPDATE_RECORDING_CALL = gql`
  mutation CreateOrUpdateRecordingCall($callSid: String!, $input: UpdateRecordingInput!) {
    createOrUpdateRecordingCall(callSid: $callSid, input: $input) {
      message
    }
  }
`;

export const MUTATION_UPDATE_CUSTOMER_BILLING_ADDRESS = gql`
  mutation UpdateCustomerBillingAddress($input: InputBillingInfo) {
    updateCustomerBillingAddress(input: $input) {
      message
      data {
        billingAddress {
          name
          company
          phone
          line1
          state
          country
          zip
        }
        vatNumber
        email
      }
    }
  }
`;

export const MUTATION_VERIFY_CALLER_ID = gql`
  mutation VerifyCallerId($input: InputVerifyCallerId) {
    verifyCallerId(input: $input) {
      message
      data {
        friendlyName
        phoneNumber
        validationCode
      }
    }
  }
`;

export const MUTATION_UPDATE_CALLER_ID = gql`
  mutation UpdateCallerId($updateCallerIdId: Int!, $input: InputUpdateVerifyCallerId) {
    updateCallerId(id: $updateCallerIdId, input: $input) {
      data {
        friendlyName
      }
      message
    }
  }
`;

export const MUTATION_DELETE_CALLER_ID = gql`
  mutation DeleteCallerId($deleteCallerIdId: Int!) {
    deleteCallerId(id: $deleteCallerIdId) {
      message
    }
  }
`;

export const ESTIMATE_UPGRADE_WORKSPACE_SUBSCRIPTION = gql`
  mutation EstimateUpgradeWorkspaceSubscription($input: InputEstimateUpgradeWorkspaceSubscription) {
    estimateUpgradeWorkspaceSubscription(input: $input) {
      message
      data {
        estimatedAmount
      }
    }
  }
`;

export const UPGRADE_WORKSPACE_SUBSCRIPTION = gql`
  mutation UpgradeWorkspaceSubscription($input: UpgradeWorkspaceSubscriptionInput) {
    upgradeWorkspaceSubscription(input: $input) {
      message
      data {
        id
      }
    }
  }
`;

export const DOWNGRADE_WORKSPACE_SUBSCRIPTION = gql`
  mutation DowngradeWorkspaceSubscription($input: DowngradeWorkspaceSubscriptionInput) {
    downgradeWorkspaceSubscription(input: $input) {
      message
      data {
        id
      }
    }
  }
`;

export const CANCEL_WORKSPACE_SUBSCRIPTION = gql`
  mutation CancelSubscriptionV2($input: CancelSubscriptionInputV2!) {
    cancelSubscriptionV2(input: $input) {
      message
      data {
        id
        billingPeriodUnit
        remainingBillingCycles
        customerId
        status
        currentTermEnd
        nextBillingAt
        createdAt
        cancelledAt
        updatedAt
        cancelScheduleCreatedAt
        deleted
        currencyCode
        subscriptionItems {
          itemPriceId
          itemType
          quantity
          unitPrice
          amount
        }
        mrr
        itemProductFamily
        itemCount
        hasScheduledChanges
        scheduledChanges {
          id
          billingPeriodUnit
          remainingBillingCycles
          customerId
          status
          currentTermEnd
          nextBillingAt
          createdAt
          cancelledAt
          updatedAt
          cancelScheduleCreatedAt
          deleted
          currencyCode
          mrr
          itemProductFamily
          itemCount
        }
        discountItems {
          discountType
          discountAmount
          discountPercentage
        }
      }
    }
  }
`;

export const RESTORE_CANCELLED_SUBSCRIPTION = gql`
  mutation RestoreCancelledSubscription {
    restoreCancelledSubscription {
      message
    }
  }
`;

export const MUTATION_SET_AUTO_RECHARGE_CONFIG = gql`
  mutation SetAutoRechargeConfig($input: InputAutoRechargeConfig) {
    setAutoRechargeConfig(input: $input) {
      message
      data {
        enabled
        creditThreshold
        credit
      }
    }
  }
`;

export const MUTATION_SET_LOW_BALANCE_ALERT = gql`
  mutation SetLowBalanceAlert($isEmail: Boolean, $balanceThreshold: Int) {
    setLowBalanceAlert(isEmail: $isEmail, balanceThreshold: $balanceThreshold) {
      message
      data {
        isEmail
        balanceThreshold
      }
    }
  }
`;

export const MUTATION_LOAD_WALLET = gql`
  mutation LoadWallet($input: LoadWalletInput!) {
    loadWallet(input: $input) {
      message
      data {
        amount
      }
    }
  }
`;

export const MUTATION_CREATE_PAYMENT_INTENT = gql`
  mutation createPaymentIntent($input: PaymentIntentInput) {
    createPaymentIntent(input: $input) {
      data
      message
    }
  }
`;

export const MUTATION_MARK_ALL_NOTIFICATION_READ = gql`
  mutation MarkAllAsReadNotification {
    markAllAsReadNotification {
      message
    }
  }
`;

export const MUTATION_MARK_NOTIFICATION_READ = gql`
  mutation ToggleReadNotification($notificationId: Int) {
    toggleReadNotification(notificationId: $notificationId) {
      message
    }
  }
`;

export const MUTATION_MARK_NOTIFICATION_SEEN = gql`
  mutation MarkAsSeenNotification($notificationId: Int) {
    markAsSeenNotification(notificationId: $notificationId) {
      message
    }
  }
`;

export const MUTATION_INVITE_MEMBER = gql`
  mutation SubscribeWorkspacePlanAndInviteUser($input: CreateSubscriptionInput) {
    subscribeWorkspacePlanAndInviteUser(input: $input) {
      message
      data {
        id
      }
    }
  }
`;

export const MUTATION_GET_ESTIMATION = gql`
  mutation EstimateMemberAddition {
    estimateMemberAddition {
      message
      data {
        estimatedAmount
      }
    }
  }
`;

export const MUTATION_MARK_ALL_NOTIFICATION_SEEN = gql`
  mutation MarkAllAsSeenNotification($notificationId: Int!) {
    markAllAsSeenNotification(notificationId: $notificationId) {
      message
    }
  }
`;

export const MUTATION_DRAFT_MESSAGE = gql`
  mutation CreateOrUpdateDraftMessage($input: InputDraftMessageConversation) {
    createOrUpdateDraftMessage(input: $input) {
      message
      data {
        id
        channelId
        clientId
        messageContent
        createdAt
        medias {
          id
          url
          originalname
          size
          key
          mimetype
        }
      }
    }
  }
`;

export const MUTATION_DELETE_CONTACTS = gql`
  mutation DeleteContacts($ids: [Int!]!) {
    deleteContacts(ids: $ids) {
      message
    }
  }
`;

export const MUTATION_DELETE_CONTACT = gql`
  mutation DeleteContact($deleteContactId: Int!) {
    deleteContact(id: $deleteContactId) {
      message
      data {
        id
        name
      }
    }
  }
`;

export const MUTATION_CREATE_CONTACT = gql`
  mutation CreateContact($input: InputContact!) {
    createContact(input: $input) {
      message
      data {
        id
        name
        email
        phoneNumber
        country {
          id
          flag
        }
        metrics {
          key
          value
        }
        tags {
          id
          label
          color
        }
        profilePicture {
          id
          url
        }
        sourceType
      }
    }
  }
`;

export const MUTATION_AUTH_ME = gql`
  mutation authMeUpdate($input: InputUser!) {
    authMe(input: $input) {
      message
      data {
        roles {
          id
          label
          slug
          level
          isActive
          isDefault
          position
        }
        id
        name
        email
        phoneNumber
        profilePicture {
          url
          id
          originalname
          size
          key
          mimetype
        }
        coverProfilePicture {
          url
        }
      }
    }
  }
`;
export const PIN_UNPIN_RECENT_CONVERSATION = gql`
  mutation PinOrUnpinRecentMessageConversation(
    $pinOrUnpinRecentMessageConversationId: Int!
    $channelId: Int!
    $input: InputPinUnpinRecentMessageConversation
  ) {
    pinOrUnpinRecentMessageConversation(
      id: $pinOrUnpinRecentMessageConversationId
      channelId: $channelId
      input: $input
    ) {
      message
      data {
        id
        pinned
        updatedAt
        client {
          id
        }
        channel {
          id
        }
      }
    }
  }
`;

export const CHANGE_PASSWORD = gql`
  mutation ChangePassword($input: InputChangePassword) {
    changePassword(input: $input) {
      message
    }
  }
`;

export const GLOBAL_SIGNOUT = gql`
  mutation GlobalSignOut {
    globalSignOut {
      message
    }
  }
`;
export const TOGGLE_CHANNEL_PIN = gql`
  mutation ToggleChannelPin($channelId: Int!) {
    toggleChannelPin(channelId: $channelId) {
      message
      data {
        id
        pinned
        isDefault
        updatedAt
      }
    }
  }
`;

export const MUTATION_CREATE_PAYMENT_SOURCE = gql`
  mutation CreatePaymentSource($input: PaymentSourceInput) {
    createPaymentSource(input: $input) {
      message
      data {
        id
        card {
          brand
          expiryMonth
          expiryYear
          firstName
          last4
          lastName
        }
        gatewayAccountId
        id
        isPrimary
        isSecondary
        paymentGateway
        paypal {
          email
        }
        referenceId
        status
      }
    }
  }
`;

export const MUTATION_SWITCH_PRIMARY_PAYMENT_SOURCE = gql`
  mutation SwitchPrimaryPaymentSource($input: SwitchPaymentSourceInput) {
    switchPrimaryPaymentSource(input: $input) {
      message
    }
  }
`;

export const MUTATION_REMOVE_SECONDARY_PAYMENT_SOURCE = gql`
  mutation RemoveSecondaryPaymentSource($input: RemoveSecondaryPaymentSourceInput) {
    removeSecondaryPaymentSource(input: $input) {
      message
    }
  }
`;

export const MUTATION_SET_SECONDARY_PAYMENT_SOURCE = gql`
  mutation SetSecondaryPaymentSource($input: SetSecondaryPaymentSourceInput) {
    setSecondaryPaymentSource(input: $input) {
      message
    }
  }
`;

export const MUTATION_UNSET_SECONDARY_PAYMENT_SOURCE = gql`
  mutation UnsetSecondaryPaymentSource($input: UnsetSecondaryPaymentSourceInput!) {
    unsetSecondaryPaymentSource(input: $input) {
      message
    }
  }
`;

export const MUTATION_DELETE_WORKSPACE_MEMBER = gql`
  mutation DeleteWorkspaceUser($deleteWorkspaceUserId: Int!) {
    deleteWorkspaceUser(id: $deleteWorkspaceUserId) {
      data {
        id
      }
      message
    }
  }
`;

export const MUTATION_REVOKE_MEMBER_INVITATION = gql`
  mutation RevokeInvitationToken($userWorkspaceId: Int) {
    revokeInvitationToken(userWorkspaceId: $userWorkspaceId) {
      data {
        id
      }
      message
    }
  }
`;

export const MUTATION_RESEND_MEMBER_INVITE = gql`
  mutation ResendInviteWorkspaceMember($input: InputResendInviteWorkspaceMember!) {
    resendInviteWorkspaceMember(input: $input) {
      message
    }
  }
`;

export const MUTATION_UPDATE_WORKSPACE_MEMBER = gql`
  mutation UpdateWorkspaceMember($input: UpdateInviteWorkspaceMember!, $updateWorkspaceMemberId: Int) {
    updateWorkspaceMember(input: $input, id: $updateWorkspaceMemberId) {
      message
    }
  }
`;

export const MUTATION_UPDATE_WORKSPACE_MEMBER_V2 = gql`
  mutation UpdateWorkspaceMemberV2($input: UpdateInviteWorkspaceMemberV2!, $updateWorkspaceMemberV2Id: Int) {
    updateWorkspaceMemberV2(input: $input, id: $updateWorkspaceMemberV2Id) {
      data {
        user {
          id
          name
          email
        }
        userWorkspace {
          id
        }
        role {
          id
          label
        }
      }
      message
    }
  }
`;

export const MUTATION_ASSIGN_MEMBERS_TO_CHANNEL = gql`
  mutation AssignMemberToChannel($input: InputChannelUserWorkspace!) {
    assignMemberToChannel(input: $input) {
      message
    }
  }
`;

export const MUTATION_SEND_MESSAGE_MEMBER = gql`
  mutation sendChatMessage($input: InputChatMessage!) {
    sendChatMessage(input: $input) {
      message
      data {
        id
        body
        isPinned
        isDeleted
        deliveryStatus
        reactions {
          emoji
          count
          userWorkspaceIds
        }
        createdAt
        type
        forwardedSource {
          ...ForwardedSourceFields
        }
        chatAttachments {
          id
          key
          mimetype
          originalname
          size
          createdAt
        }
        repliedTo {
          id
          body
          isDeleted
          senderId
          senderName
          chatAttachments {
            id
            key
            mimetype
            originalname
            size
            createdAt
          }
        }
        messageConversation {
          id
          conversationStatus
          effectiveStartedAt
          effectiveEndedAt
        }
        sender {
          id
          status
          userOnlineManagement {
            isActive
            lastSeenAt
            activeStatus
            awayTill
            message
            awaySet
            icon
          }
          user {
            id
            name
            email
            phoneNumber
            profilePicture {
              id
              url
            }
            address
            dueIn
            accountStatus
            timezone
            averageRiskScore
            totalMonthlyFee
            lastPasswordResetAt
          }
        }
      }
    }
  }
  ${FORWARDED_SOURCE_FIELDS}
`;

export const MUTATION_DELETE_MEMBER_MESSAGE = gql`
  mutation DeleteChatMessage($deleteChatMessageId: Int!) {
    deleteChatMessage(id: $deleteChatMessageId) {
      message
      data {
        id
        body
        isPinned
        isDeleted
      }
    }
  }
`;

export const MUTATION_PIN_UNPIN_MEMBER_CONVERSATION = gql`
  mutation PinOrUnpinChatMessage($pinOrUnpinChatMessageId: Int!, $input: InputPinOrUnpinChatMessage) {
    pinOrUnpinChatMessage(id: $pinOrUnpinChatMessageId, input: $input) {
      message
      data {
        id
        isPinned
      }
    }
  }
`;

export const ASSIGN_OR_UNASSIGN_CHANNEL_TO_MEMBER = gql`
  mutation AssignOrUnassignChannelToMember($input: InputAssignOrUnassignChannelToMember) {
    assignOrUnassignChannelToMember(input: $input) {
      message
    }
  }
`;

export const VERIFY_PAYMENT_REQUEST = gql`
  mutation VerifyPaymentRequest($urlToken: String!) {
    verifyPaymentRequest(urlToken: $urlToken) {
      message
      data {
        amount
        purpose
      }
    }
  }
`;

export const COLLECT_PAYMENT_REQUEST = gql`
  mutation CollectPaymentRequest($input: CollectPaymentRequestInput!) {
    collectPaymentRequest(input: $input) {
      message
    }
  }
`;

export const READ_MESSAGE = gql`
  mutation ReadMessage($messageConversationId: Int) {
    readMessage(messageConversationId: $messageConversationId) {
      message
    }
  }
`;

export const READ_ALL_MESSAGE = gql`
  mutation ReadAllMessage($recentMessageConversationId: Int) {
    readAllMessage(recentMessageConversationId: $recentMessageConversationId) {
      message
    }
  }
`;

export const MUTATION_MARK_USER_ACTIVE_AWAY = gql`
  mutation MarkAsActiveOrAway($input: InputMarkAsActiveOrAway) {
    markAsActiveOrAway(input: $input) {
      message
    }
  }
`;
export const EXPORT_INVOICES = gql`
  mutation ExportInvoices($transactionDateFrom: Date!, $transactionDateTo: Date!) {
    exportInvoices(transactionDateFrom: $transactionDateFrom, transactionDateTo: $transactionDateTo) {
      message
    }
  }
`;
export const MUTATION_ADD_TO_CART = gql`
  mutation AddToCart($input: InputCart) {
    addToCart(input: $input) {
      message
    }
  }
`;

export const MUTATION_REMOVE_FROM_CART = gql`
  mutation RemoveFromCart($input: InputCart) {
    removeFromCart(input: $input) {
      message
    }
  }
`;

export const MUTATION_PURCHASE_NUMBER = gql`
  mutation SubscribeNumberPlan($input: NumberSubscriptionInput!) {
    subscribeNumberPlan(input: $input) {
      message
    }
  }
`;

export const REQUEST_REGULATORY_COMPLIANCE = gql`
  mutation RequestRegulatoryCompliance($input: InputRequestRegulatoryCompliance) {
    requestRegulatoryCompliance(input: $input) {
      message
    }
  }
`;

export const REQUEST_FREE_REGULATORY_COMPLIANCE = gql`
  mutation RequestFreeRegulatoryCompliance($input: InputRequestFreeRegulatoryCompliance!) {
    requestFreeRegulatoryCompliance(input: $input) {
      message
      data {
        id
        status
        claimIntent
      }
    }
  }
`;

export const MUTATION_CLAIM_FREE_NUMBERS = gql`
  mutation ClaimFreeNumbers($input: NumbersClaimInput!) {
    claimFreeNumbers(input: $input) {
      message
    }
  }
`;

export const MUTATION_CALL_TRANSFER = gql`
  mutation CallTransfer($callSid: String!, $input: InputCallTransfer!) {
    callTransfer(callSid: $callSid, input: $input) {
      message
    }
  }
`;

export const MUTATION_RESEND_MESSAGE = gql`
  mutation ResendMessage($resendMessageId: Int!) {
    resendMessage(id: $resendMessageId) {
      message
      data {
        id
        messageConversationErrorLog {
          code
          message
          statusCode
        }
        numMedia
        friendlyName
        forwardedAt
        forwardedTo {
          id
          externalNumber
          channel {
            id
            friendlyName
            phoneNumber
          }
        }
        agent {
          channels {
            friendlyName
            phoneNumber
            identity
            id
          }
        }
        channelId
        client {
          id
          name
          phoneNumber
          profilePicture {
            url
          }
        }
        agent {
          id
          status
          user {
            id
            name
            profilePicture {
              url
            }
          }
          userOnlineManagement {
            activeStatus
            isActive
            lastSeenAt
          }
        }
        conversationStatus
        conversationType
        direction
        id
        markAsClosed
        messageContent
        pinned
        updatedAt
        createdAt
        recordingUrl
        voicemailRecordingUrl
        durationConference {
          startDate
          endDate
          id
        }
        voicemailRecording {
          id
          url
        }
        callRecording {
          id
          transcription {
            id
            providerCallRecordingsId
            transcribedText
            targetLanguageCode
            sentiment
            callSummary
            jobStatus
            jobResponse
            jobKeyName
          }
        }
      }
    }
  }
`;

export const MUTATION_CONNECT_THIRD_PARTY_INTEGRATION = gql`
  mutation ConnectThirdParty($integrationId: Int) {
    connectThirdParty(integrationId: $integrationId) {
      message
      data {
        id
        name
        description
        domain
        logoUrl
        label
        integrationUser {
          id
          status
        }
      }
    }
  }
`;

export const MUTATION_DISCONNECT_THIRD_PARTY_INTEGRATION = gql`
  mutation DisconnectThirdParty($integrationId: Int) {
    disconnectThirdParty(integrationId: $integrationId) {
      message
      data {
        id
        name
        description
        domain
        logoUrl
        label
        integrationUser {
          id
          status
        }
      }
    }
  }
`;

export const MUTATION_CREATE_INTEGRATION_CONFIGURATION = gql`
  mutation CreateIntegrationConfiguration($input: InputIntegrationConfiguration) {
    createIntegrationConfiguration(input: $input) {
      message
      data {
        id
        integrationUserId
        workspaceId
        syncCallDetails
        syncContacts
        syncMessage
        syncVoicemails
        syncRecordings
        createdBy
        updatedBy
      }
    }
  }
`;

export const MUTATION_CREATE_INTEGRATION_WEBHOOKS = gql`
  mutation CreateIntegrationWebhooks($integrationUserId: Int!, $input: [InputIntegrationWebhook]) {
    createIntegrationWebhooks(integrationUserId: $integrationUserId, input: $input) {
      message
      data {
        id
        integrationUserId
        label
        username
        password
        url
      }
    }
  }
`;

export const MUTATION_UPDATE_INTEGRATION_WEBHOOKS = gql`
  mutation UpdateIntegrationWebhooks($input: [InputUpdateIntegrationWebhook]) {
    updateIntegrationWebhooks(input: $input) {
      message
      data {
        id
        integrationUserId
        label
        username
        password
        url
      }
    }
  }
`;

export const MUTATION_TOGGLE_INTEGRATION_CHANNEL_STATUS = gql`
  mutation ToggleIntegrationChannelStatus($input: InputIntegrationChannel) {
    toggleIntegrationChannelStatus(input: $input) {
      message
      data {
        id
        friendlyName
        phoneNumber
        country {
          id
          flag
        }
        integrationChannels {
          id
          integrationUserId
          isEnabled
        }
      }
    }
  }
`;

export const MUTATION_SET_INTEGRATION_TRIGGER_GROUP = gql`
  mutation SetIntegrationTriggerGroup($integrationId: Int!, $groupId: Int!, $isActive: Boolean!, $channelIds: [Int!]) {
    setIntegrationTriggerGroup(
      integrationId: $integrationId
      groupId: $groupId
      isActive: $isActive
      channelIds: $channelIds
    ) {
      id
      state
    }
  }
`;

export const MUTATION_SYNC_APP_CONTACT_TO_CRM = gql`
  mutation SyncAppContactToCRM($integrationId: Int!) {
    syncAppContactToCRM(integrationId: $integrationId) {
      started
    }
  }
`;

export const MUTATION_SYNC_CRM_CONTACT_TO_APP = gql`
  mutation SyncCRMContactToApp($integrationId: Int!) {
    syncCRMContactToApp(integrationId: $integrationId) {
      started
    }
  }
`;

export const MUTATION_REVOKE_INTEGRATION = gql`
  mutation Revoke($integrationId: Int!) {
    revoke(integrationId: $integrationId) {
      message
      data {
        id
        name
        description
        domain
        logoUrl
        label
        integrationUser {
          id
          status
        }
      }
    }
  }
`;

export const MUTATION_INTEGRATE_INTEGRATION = gql`
  mutation Integrate($integrationId: Int!) {
    integrate(integrationId: $integrationId) {
      message
      data {
        authorizationUrl
      }
    }
  }
`;

export const MUTATION_EXCHANGE_OAUTH_AUTHORIZATION_CODE_FOR_TOKENS = gql`
  mutation ExchangeOAuthAuthorizationCodeForTokens($input: AuthorizeOAuthIntegrationInput) {
    exchangeOAuthAuthorizationCodeForTokens(input: $input) {
      message
      data {
        id
        name
        description
        domain
        logoUrl
        label
        integrationUser {
          id
          status
        }
      }
    }
  }
`;

export const MUTATION_LOG_OAUTH_AUTHORIZATION_ERROR = gql`
  mutation LogOAuthAuthorizationError($input: LogOAuthAuthorizationErrorInput) {
    logOAuthAuthorizationError(input: $input) {
      message
    }
  }
`;

export const MUTATION_CACHE_OATH_AUTHORIZATION_CODE = gql`
  mutation CacheOAuthAuthorizationCode($input: CacheIntegrationOAuthCodeInput) {
    cacheOAuthAuthorizationCode(input: $input) {
      data {
        transientToken
      }
      message
    }
  }
`;

export const MUTATION_EXCHANGE_OAUTH_AUTHORIZATION_CODE_FOR_TOKENS_VIA_TRANSIENT_TOKEN = gql`
  mutation ExchangeOAuthAuthorizationCodeForTokensViaTransientToken($input: AuthorizeOAuthIntegrationWithTokenInput) {
    exchangeOAuthAuthorizationCodeForTokensViaTransientToken(input: $input) {
      message
    }
  }
`;

export const MUTATION_WORKSPACE_AUTH_ME = gql`
  mutation WorkspaceAuthMe($input: InputUpdateWorkspace) {
    workspaceAuthMe(input: $input) {
      message
      data {
        id
        coverProfile {
          url
          id
        }
        logo {
          id
          url
        }
        label
        timezoneId
        timezoneLabel
        timezoneValue
        timezone
      }
    }
  }
`;
export const MUTATION_CREATE_INTEGRATION_REQUEST = gql`
  mutation CreateIntegrationRequest($input: CreateIntegrationRequestInput!) {
    createIntegrationRequest(input: $input) {
      data {
        appName
        id
      }
      message
    }
  }
`;

export const MUTATION_CREATE_BUSINESS_ADDRESS = gql`
  mutation CreateBusinessAddress($input: InputBusinessAddress) {
    createBusinessAddress(input: $input) {
      data {
        id
      }
      message
    }
  }
`;

export const MUTATION_CREATE_CALL_SETTINGS = gql`
  mutation CreateCallSetting($input: InputCallSetting!) {
    createCallSetting(input: $input) {
      data {
        id
        friendlyName
      }
      message
    }
  }
`;
export const MUTATION_CALL_FORWARDING = gql`
  mutation SetCallForwardInCallSetting($input: InputCallForward!) {
    setCallForwardInCallSetting(input: $input) {
      message
      data {
        id
        isUnconditional
        targets {
          id
          phoneNumber
          priority
          channel {
            id
            friendlyName
          }
        }
        isActive
      }
    }
  }
`;

export const MUTATION_SET_CALL_SETTINGS_VOICEMAIL = gql`
  mutation SetVoicemailInCallSetting($setCallsettingVoicemailId: Int!, $input: InputCallSettingVoiceMail!) {
    setVoicemailInCallSetting(id: $setCallsettingVoicemailId, input: $input) {
      data {
        id
        audioPrompt {
          audio {
            id
            url
          }
          id
          gender
          language
          text
          type
        }
        isActive
        sendVoicemailToEmail
      }
      message
    }
  }
`;

export const MUTATION_CREATE_UPDATE_BUSINESS_HOUR = gql`
  mutation SetBusinessHoursInCallSetting($input: InputCallSettingBusinessHour!) {
    setBusinessHoursInCallSetting(input: $input) {
      message
    }
  }
`;

export const MUTATION_CREATE_UPDATE_AFTER_HOUR = gql`
  mutation SetBusinessAfterHoursInCallSetting($input: InputCallSettingBusinessAfterHours!) {
    setBusinessAfterHoursInCallSetting(input: $input) {
      message
    }
  }
`;

export const MUTATION_SCHEDULE_MESSAGE = gql`
  mutation ScheduleMessage($input: InputMessageSchedule) {
    scheduleMessage(input: $input) {
      message
      data {
        attachments {
          createdAt
          id
          key
          mimetype
          originalname
          size
          url
        }
        id
        failureReason
        messageContent
        scheduleAt
        status
        client {
          id
        }
        channel {
          id
        }
        createdAt
      }
      message
    }
  }
`;

export const MUTATION_CREATE_UPDATE_IVR = gql`
  mutation SetIvrInCallSetting($callSettingId: Int!, $input: InputIvr!) {
    setIvrInCallSetting(callSettingId: $callSettingId, input: $input) {
      message
      data {
        id
      }
    }
  }
`;

export const MUTATION_DELETE_SCHEDULED_MESSAGE = gql`
  mutation DeleteScheduledMessage($deleteScheduledMessageId: String!) {
    deleteScheduledMessage(id: $deleteScheduledMessageId) {
      message
    }
  }
`;

export const MUTATION_SEND_SCHEDULE_MESSAGE_NOW = gql`
  mutation SendScheduledMessageNow($sendScheduledMessageNowId: String!) {
    sendScheduledMessageNow(id: $sendScheduledMessageNowId) {
      message
    }
  }
`;

export const MUTATION_UPDATE_SCHEDULED_MESSAGE = gql`
  mutation UpdateScheduledMessage($updateScheduledMessageId: String!, $input: UpdateMessageSchedule!) {
    updateScheduledMessage(id: $updateScheduledMessageId, input: $input) {
      message
    }
  }
`;
export const MUTATION_COLLECT_PAYMENT = gql`
  mutation CollectPayment($paymentSourceId: String!, $collectPaymentId: String) {
    collectPayment(paymentSourceId: $paymentSourceId, id: $collectPaymentId) {
      message
      data
    }
  }
`;

export const MUTATION_CREATE_UPDATE_CALL_TRANSFER = gql`
  mutation SetCallTransferInCallSetting($callSettingId: Int!, $input: InputCallSettingTransfer!) {
    setCallTransferInCallSetting(callSettingId: $callSettingId, input: $input) {
      message
    }
  }
`;

export const MUTATION_SET_CALLER_ID = gql`
  mutation SetCallerIdInCallSetting($callerId: Int, $setCallerIdInCallSettingId: Int!) {
    setCallerIdInCallSetting(callerId: $callerId, id: $setCallerIdInCallSettingId) {
      message
    }
  }
`;

export const MUTATION_RING_DURATION = gql`
  mutation SetTimeoutInCallSetting($setTimeoutInCallSettingId: Int!, $input: InputTimeout!) {
    setTimeoutInCallSetting(id: $setTimeoutInCallSettingId, input: $input) {
      message
      data {
        id
        timeout
      }
    }
  }
`;

export const MUTATION_CALLER_TUNE = gql`
  mutation SetCallerTuneInCallSetting($setCallerTuneInCallSettingId: Int!, $input: InputCallerTune!) {
    setCallerTuneInCallSetting(id: $setCallerTuneInCallSettingId, input: $input) {
      message
      data {
        id
        isActive
        audioPrompt {
          id
          workspaceId
          text
          language
          gender
          audio {
            id
            url
            originalname
            size
            key
            mimetype
            createdAt
          }
          type
        }
        callerTuneLoop
      }
    }
  }
`;

export const MUTATION_SUBSCRIBE_NUMBER_PLAN = gql`
  mutation SubscribeNumberPlan($input: NumberSubscriptionInput!) {
    subscribeNumberPlan(input: $input) {
      message
    }
  }
`;

export const MUTATION_WELCOME_MESSAGE = gql`
  mutation SetWelcomeMessageInCallSetting(
    $setWelcomeMessageInCallSettingId: Int!
    $input: InputCallSettingWelcomeMessage!
  ) {
    setWelcomeMessageInCallSetting(id: $setWelcomeMessageInCallSettingId, input: $input) {
      message
      data {
        id
        isActive
        audioPrompt {
          id
          workspaceId
          text
          language
          gender
          audio {
            id
            url
            originalname
            size
            key
            mimetype
            createdAt
          }
          type
        }
      }
    }
  }
`;

export const MUTATION_SET_CHANNEL_GROUP_IN_CALL_SETTING = gql`
  mutation SetChannelGroupsInCallSetting($setChannelGroupsInCallSettingId: Int!, $channelGroupIds: [Int]!) {
    setChannelGroupsInCallSetting(id: $setChannelGroupsInCallSettingId, channelGroupIds: $channelGroupIds) {
      message
      data {
        slug
        name
        id
      }
    }
  }
`;

export const MUTATION_DELETE_CALL_SETTING = gql`
  mutation DeleteCallSetting($deleteCallSettingId: Int!) {
    deleteCallSetting(id: $deleteCallSettingId) {
      message
    }
  }
`;

export const MUTATION_CREATE_CONTACT_COMPLIANCE_CONSENT = gql`
  mutation CreateContactComplianceConsent($input: InputContactComplianceConsent!) {
    createContactComplianceConsent(input: $input) {
      message
      data {
        id
      }
    }
  }
`;

export const MUTATION_NUMBER_SET_CALLER_ID = gql`
  mutation SetCallerIdInChannelSetting($channelId: Int!, $callerId: Int) {
    setCallerIdInChannelSetting(channelId: $channelId, callerId: $callerId) {
      message
    }
  }
`;

export const MUTATION_NUMBER_CALL_FORWARDING = gql`
  mutation SetCallForwardInChannelSetting($input: InputCallForwardChannel!, $channelId: Int) {
    setCallForwardInChannelSetting(input: $input, channelId: $channelId) {
      message
      data {
        id
        isUnconditional
        isActive
        targets {
          id
          phoneNumber
          priority
          channel {
            id
            friendlyName
          }
        }
      }
    }
  }
`;

export const MUTATION_SET_AUTO_RECORDING_IN_CALL_SETTING = gql`
  mutation SetAutoRecordingInCallSetting($setAutoRecordingInCallSettingId: Int!, $input: InputAutoRecording!) {
    setAutoRecordingInCallSetting(id: $setAutoRecordingInCallSettingId, input: $input) {
      message
      data {
        id
        autoRecording
      }
    }
  }
`;

export const MUTATION_SET_AUTO_TRANSCRIBE_IN_CALL_SETTING = gql`
  mutation SetAutoTranscribeInCallSetting($setAutoTranscribeInCallSettingId: Int!, $input: InputAutoTranscribe!) {
    setAutoTranscribeInCallSetting(id: $setAutoTranscribeInCallSettingId, input: $input) {
      message
      data {
        id
        autoTranscribe
      }
    }
  }
`;

export const MUTATION_SET_RECORDING_ANNOUNCEMENT_IN_CALL_SETTING = gql`
  mutation SetRecordingAnnouncementInCallSetting(
    $setRecordingAnnouncementInCallSettingId: Int!
    $input: InputRecordingAnnouncement!
  ) {
    setRecordingAnnouncementInCallSetting(id: $setRecordingAnnouncementInCallSettingId, input: $input) {
      message
      data {
        recordingAnnouncementMessage
        recordingAnnouncementAfterBeep
        id
        audioPrompt {
          id
          text
          gender
          audio {
            url
            id
          }
        }
      }
    }
  }
`;

export const MUTATION_NUMBER_WELCOME_MESSAGE = gql`
  mutation SetWelcomeMessageInChannelSetting($channelId: Int!, $input: InputCallSettingWelcomeMessage!) {
    setWelcomeMessageInChannelSetting(channelId: $channelId, input: $input) {
      message
      data {
        id
        isActive
        audioPrompt {
          id
          workspaceId
          text
          language
          gender
          audio {
            id
            url
            originalname
            size
            key
            mimetype
            createdAt
          }
          type
        }
      }
    }
  }
`;

export const MUTATION_NUMBER_RING_DURATION = gql`
  mutation SetTimeoutInChannelSetting($channelId: Int!, $input: InputTimeout!) {
    setTimeoutInChannelSetting(channelId: $channelId, input: $input) {
      message
      data {
        id
        timeout
      }
    }
  }
`;

export const MUTATION_RING_GROUP = gql`
  mutation SetRingGroupInCallSetting($callSettingId: Int!, $input: InputRingGroup!) {
    setRingGroupInCallSetting(callSettingId: $callSettingId, input: $input) {
      message
      data {
        id
      }
    }
  }
`;

export const MUTATION_NUMBER_RING_GROUP = gql`
  mutation SetRingGroupInChannelSetting($channelId: Int!, $input: InputRingGroup!) {
    setRingGroupInChannelSetting(channelId: $channelId, input: $input) {
      message
      data {
        id
      }
    }
  }
`;

export const MUTATION_NUMBER_CALLER_TUNE = gql`
  mutation SetCallerTuneInChannelSetting($channelId: Int!, $input: InputCallerTune!) {
    setCallerTuneInChannelSetting(channelId: $channelId, input: $input) {
      message
      data {
        id
        isActive
        audioPrompt {
          id
          workspaceId
          text
          language
          gender
          audio {
            id
            url
            originalname
            size
            key
            mimetype
            createdAt
          }
          type
        }
        callerTuneLoop
      }
    }
  }
`;

export const MUTATION_SET_CHANNEL_SETTINGS_VOICEMAIL = gql`
  mutation SetVoicemailInChannelSetting($channelId: Int!, $input: InputCallSettingVoiceMail!) {
    setVoicemailInChannelSetting(channelId: $channelId, input: $input) {
      message
      data {
        id
        audioPrompt {
          audio {
            id
            url
          }
          id
          gender
          language
          text
          type
        }
        isActive
        sendVoicemailToEmail
      }
    }
  }
`;
export const MUTATION_NUMBER_CREATE_UPDATE_CALL_TRANSFER = gql`
  mutation SetCallTransferInChannelSetting($channelId: Int!, $input: InputChannelSettingCallTransfer!) {
    setCallTransferInChannelSetting(channelId: $channelId, input: $input) {
      message
    }
  }
`;

export const MUTATION_UPDATE_CHANNEL = gql`
  mutation UpdateChannel($updateChannelId: Int!, $input: InputChannel!) {
    updateChannel(id: $updateChannelId, input: $input) {
      message
      data {
        id
        friendlyName
        phoneNumber
      }
    }
  }
`;
export const MUTATION_NUMBER_SET_AUTO_RECORDING_IN_CALL_SETTING = gql`
  mutation SetAutoRecordingInChannelSetting($channelId: Int!, $input: InputAutoRecording!) {
    setAutoRecordingInChannelSetting(channelId: $channelId, input: $input) {
      message
      data {
        id
        autoRecording
      }
    }
  }
`;

export const MUTATION_NUMBER_SET_AUTO_TRANSCRIBE_IN_CALL_SETTING = gql`
  mutation SetAutoTranscribeInChannelSetting($channelId: Int!, $input: InputAutoTranscribe!) {
    setAutoTranscribeInChannelSetting(channelId: $channelId, input: $input) {
      message
      data {
        id
        autoTranscribe
      }
    }
  }
`;

export const MUTATION_NUMBER_SET_RECORDING_ANNOUNCEMENT_IN_CALL_SETTING = gql`
  mutation SetRecordingAnnouncementInChannelSetting($channelId: Int!, $input: InputRecordingAnnouncement!) {
    setRecordingAnnouncementInChannelSetting(channelId: $channelId, input: $input) {
      message
      data {
        recordingAnnouncementMessage
        recordingAnnouncementAfterBeep
        id
        audioPrompt {
          id
          text
          gender
          audio {
            url
            id
          }
        }
      }
    }
  }
`;

export const MUTATION_NUMBER_CREATE_UPDATE_IVR = gql`
  mutation SetIvrInChannelSetting($channelId: Int!, $input: InputChannelSettingIvr!) {
    setIvrInChannelSetting(channelId: $channelId, input: $input) {
      message
      data {
        id
      }
    }
  }
`;

export const MUTATION_NUMBER_CREATE_UPDATE_BUSINESS_HOUR = gql`
  mutation SetBusinessHoursInChannelSetting($channelId: Int!, $input: InputChannelSettingBusinessHour!) {
    setBusinessHoursInChannelSetting(channelId: $channelId, input: $input) {
      message
    }
  }
`;

export const MUTATION_CREATE_CHANNEL_GROUP = gql`
  mutation CreateChannelGroup($input: InputChannelGroup!) {
    createChannelGroup(input: $input) {
      message
      data {
        slug
        name
        id
      }
    }
  }
`;

export const MUTATION_DELETE_CHANNEL_GROUP = gql`
  mutation DeleteChannelGroup($deleteChannelGroupId: Int!) {
    deleteChannelGroup(id: $deleteChannelGroupId) {
      message
    }
  }
`;

export const MUTATION_UPDATE_CHANNEL_GROUP = gql`
  mutation UpdateChannelGroup($updateChannelGroupId: Int!, $input: InputChannelGroup!) {
    updateChannelGroup(id: $updateChannelGroupId, input: $input) {
      message
    }
  }
`;

export const MUTATION_ASSIGN_CHANNELS_TO_GROUPS = gql`
  mutation SetChannelsInGroup($input: InputSetChannelsInGroup!) {
    setChannelsInGroup(input: $input) {
      message
      data {
        unAssignedChannels {
          phoneNumber
          friendlyName
          id
        }
        assignedChannels {
          phoneNumber
          friendlyName
          id
        }
      }
    }
  }
`;

export const MUTATION_NUMBER_CREATE_UPDATE_AFTER_HOUR = gql`
  mutation SetBusinessAfterHoursInChannelSetting($channelId: Int!, $input: InputChannelSettingBusinessAfterHours!) {
    setBusinessAfterHoursInChannelSetting(channelId: $channelId, input: $input) {
      message
    }
  }
`;

export const MUTATION_SWITCH_TO_CHANNEL_SETTING = gql`
  mutation SwitchToChannelSetting($channelId: Int!) {
    switchToChannelSetting(channelId: $channelId) {
      message
      data {
        id
        friendlyName
        phoneNumber
        country {
          id
          flag
          alphaTwoCode
        }
        callSetting {
          id
          businessHourType
          friendlyName
          incomingTimeout
          recording
          metrics {
            key
            value
          }
          callerId {
            id
            friendlyName
            phoneNumber
          }
        }
        channelGroup {
          id
          name
          callSetting {
            id
            businessHourType
            metrics {
              key
              value
            }
            friendlyName
            recording
            incomingTimeout
            callerId {
              id
              friendlyName
              phoneNumber
            }
          }
        }
        assignedUsers {
          id
          name
          profilePicture {
            id
            url
          }
        }
      }
    }
  }
`;

export const MUTATION_SET_CALL_SETTING_IN_CHANNEL_GROUP = gql`
  mutation SetCallSettingInChannelGroup($setCallSettingInChannelGroupId: Int!, $callSettingId: Int) {
    setCallSettingInChannelGroup(id: $setCallSettingInChannelGroupId, callSettingId: $callSettingId) {
      message
      data {
        slug
        name
        metrics {
          value
          key
        }
        id
        channel {
          phoneNumber
          id
          friendlyName
        }
        callSetting {
          id
          friendlyName
          metrics {
            value
            key
          }
        }
      }
    }
  }
`;

export const MUTATION_TOGGLE_CHANNEL_NOTIFICATION = gql`
  mutation ToggleChannelNotification($input: InputToggleChannelNotification!) {
    toggleChannelNotification(input: $input) {
      message
    }
  }
`;

export const EXPORT_ALL_CONTACTS = gql`
  mutation ExportContacts($query: String, $tags: [Int], $countryCode: [String], $sourceType: [ContactSourceTypeEnum]) {
    exportContacts(query: $query, tags: $tags, countryCode: $countryCode, sourceType: $sourceType) {
      message
    }
  }
`;

export const MUTATION_FORWARD_CHAT = gql`
  mutation ForwardChatToEmail($input: InputForwardChatToEmail!) {
    forwardChatToEmail(input: $input) {
      message
    }
  }
`;

export const VERIFY_INVITATION_TOKEN = gql`
  mutation VerifyInvitationToken($input: InputVerifyInvitationToken) {
    verifyInvitationToken(input: $input) {
      data {
        name
        passwordRequired
      }
      message
    }
  }
`;

export const ACCEPT_INVITATION = gql`
  mutation AcceptInvitedWorkspaceMember($input: InputAcceptInvitedWorkspaceMemberInterface) {
    acceptInvitedWorkspaceMember(input: $input) {
      message
    }
  }
`;

export const MUTATION_WRAP_UP_CALL = gql`
  mutation WrapUpCall($callSid: String!) {
    wrapUpCall(callSid: $callSid) {
      message
    }
  }
`;

export const MUTATION_VERIFY_TRANSITION_STATUS = gql`
  mutation VerifyAndTransitionStatus($verifyAndTransitionStatusId: Int!, $model: VerifyAndTransitionModelEnum) {
    verifyAndTransitionStatus(id: $verifyAndTransitionStatusId, model: $model) {
      message
    }
  }
`;

export const MUTATION_REMOVE_PROVIDER_PHONE_NUMBER = gql`
  mutation RemoveProviderPhoneNumber($removeProviderPhoneNumberId: Int!) {
    removeProviderPhoneNumber(id: $removeProviderPhoneNumberId) {
      message
    }
  }
`;

export const MUTATION_REMOVE_PROVIDER_PHONE_NUMBERS = gql`
  mutation RemoveProviderPhoneNumbers($ids: [Int!]!) {
    removeProviderPhoneNumbers(ids: $ids) {
      message
      deletedIds
      failed {
        id
        reason
      }
    }
  }
`;

export const MUTATION_CREATE_TRANSCRIPTION_JOB = gql`
  mutation CreateTranscriptionJob($input: InputTranscriptionInformation!) {
    createTranscriptionJob(input: $input) {
      message
      data {
        jobStatus
        id
        providerCallRecordingsId
      }
    }
  }
`;
export const MUTATION_SWITCH_DESIGN = gql`
  mutation SwitchUiVersion($uiVersion: UiVersionEnum!) {
    switchUiVersion(uiVersion: $uiVersion) {
      message
    }
  }
`;

export const MUTATION_PERMANENT_V2_SWITCH = gql`
  mutation MigrateToV2 {
    migrateToV2 {
      message
    }
  }
`;

export const MUTATION_UPDATE_CALL_SETTING = gql`
  mutation UpdateCallSetting($updateCallSettingId: Int!, $input: InputCallSetting!) {
    updateCallSetting(id: $updateCallSettingId, input: $input) {
      data {
        id
        friendlyName
      }
      message
    }
  }
`;

export const MUTATION_WHISPER_CALL_START = gql`
  mutation SuperviseConference($superviseConferenceId: Int!, $input: InputSuperviseConference) {
    superviseConference(id: $superviseConferenceId, input: $input) {
      message
      data {
        supervisionId
      }
    }
  }
`;
export const MUTATION_TAKE_OVER_AI_CALL = gql`
  mutation TakeOverAiCall($id: Int!, $input: InputTakeOverAiCall) {
    takeOverAiCall(id: $id, input: $input) {
      message
      data {
        supervisionId
        supervisionMode
      }
    }
  }
`;
export const MUTATION_WHISPER_CALL_END = gql`
  mutation UnSuperviseConference($unSuperviseConferenceId: Int!, $input: InputUnSuperviseConference) {
    unSuperviseConference(id: $unSuperviseConferenceId, input: $input) {
      message
      data {
        supervisionId
      }
    }
  }
`;
export const MUTATION_TERMINATE_CALL = gql`
  mutation HangUpConference($hangUpConferenceId: Int!, $input: InputHangUpConference) {
    hangUpConference(id: $hangUpConferenceId, input: $input) {
      message
    }
  }
`;

export const CAPTURE_CALL_CLIENT = gql`
  mutation CaptureClientCallEntityInfo($callSid: String!, $input: InputCollectClientInformation!) {
    captureClientCallEntityInfo(callSid: $callSid, input: $input) {
      message
    }
  }
`;

export const MUTATION_FEDERATED_AUTH = gql`
  mutation FederatedAuth($input: InputFederatedAuth!) {
    federatedAuth(input: $input) {
      message
      data {
        token {
          sessionId
          access
          accessTokenIssuedAt
          accessTokenExpiresAt
          refresh
          refreshTokenIssuedAt
          refreshTokenExpiresAt
        }
        name
        id
        email
      }
    }
  }
`;

export const MUTATION_SETUP_TOTP_MFA = gql`
  mutation SetUpTOTPMfa {
    setUpTOTPMfa {
      message
      data {
        otpAuthUri
      }
    }
  }
`;

export const MUTATION_VERIFY_AND_ENABLE_TOTP_MFA = gql`
  mutation VerifyAndEnableTOTPMfa($input: InputEnabledTOTPMfa!) {
    verifyAndEnableTOTPMfa(input: $input) {
      message
      data {
        status
      }
    }
  }
`;

export const MUTATION_DISABLE_TOTP_MFA = gql`
  mutation DisableTOTPMfa {
    disableTOTPMfa {
      message
      data {
        status
      }
    }
  }
`;

export const MUTATION_SETUP_EMAIL_MFA = gql`
  mutation SetUpEmailMfa($input: InputEmailMfa!) {
    setUpEmailMfa(input: $input) {
      message
      data {
        status
      }
    }
  }
`;

export const MUTATION_SWITCH_TO_EMAIL_MFA = gql`
  mutation SwitchToEmailMfa($input: InputSwitchEmailMfa!) {
    switchToEmailMfa(input: $input) {
      message
      data {
        challengeName
      }
    }
  }
`;

export const MUTATION_RESEND_EMAIL_MFA_OTP = gql`
  mutation ResendEmailMfaOtp($input: InputSwitchEmailMfa!) {
    resendEmailMfaOtp(input: $input) {
      message
      data {
        challengeName
      }
    }
  }
`;

export const MUTATION_VERIFY_MFA_AUTHENTICATION = gql`
  mutation VerifyMfaAuthentication($input: InputMfaAuth!) {
    verifyMfaAuthentication(input: $input) {
      message
      data {
        token {
          sessionId
          access
          accessTokenIssuedAt
          accessTokenExpiresAt
          refresh
          refreshTokenIssuedAt
          refreshTokenExpiresAt
        }
        isEmailVerified
        twoFactorAuthenticationRequired
        challengeName
        name
        email
        id
      }
    }
  }
`;

export const MUTATION_SET_MFA_REMINDER = gql`
  mutation SetMfaReminder {
    setMfaReminder {
      message
    }
  }
`;

export const MUTATION_SET_TOUR_GUIDE_ENABLED = gql`
  mutation SetTourGuideEnabled($tourGuideEnabled: Boolean!) {
    setTourGuideEnabled(tourGuideEnabled: $tourGuideEnabled) {
      message
    }
  }
`;

export const MUTATION_SET_WORKSPACE_PREFERRED_CONTACT_METHODS = gql`
  mutation SetWorkspacePreferredContactMethods($input: InputWorkspacePreferredContactMethod) {
    setWorkspacePreferredContactMethods(input: $input) {
      message
    }
  }
`;
export const MUTATION_SET_WORKSPACE_PREFERRED_CONTACT_METHOD_LATER = gql`
  mutation SetWorkspacePreferredContactMethodsLater {
    setWorkspacePreferredContactMethodsLater {
      message
    }
  }
`;

export const MUTATION_CREATE_KYC_VERIFICATION_SESSION = gql`
  mutation CreateKycVerificationSession {
    createKycVerificationSession {
      data {
        id
        sessionUrl
        sessionExpiry
        sessionId
        status
        vendorData
        workflowId
      }
      message
    }
  }
`;
export const MUTATION_SET_USAGE_REPORT_NOTIFICATION = gql`
  mutation SetUsageReportNotification($input: InputSetUsageReportNotification!) {
    setUsageReportNotification(input: $input) {
      message
    }
  }
`;
export const MUTATION_MARK_MEMBER_CHAT_AS_READ = gql`
  mutation MarkMemberChatAsRead($roomId: Int!) {
    markMemberChatAsRead(roomId: $roomId) {
      message
    }
  }
`;

export const MUTATION_SWITCH_CALL = gql`
  mutation SwitchCall($switchCallId: Int!, $input: InputSwitchCall!) {
    switchCall(id: $switchCallId, input: $input) {
      message
    }
  }
`;
export const MUTATION_CANCEL_WORKSPACE_DOWNGRADE = gql`
  mutation CancelWorkspaceDowngrade {
    cancelWorkspaceDowngrade {
      message
    }
  }
`;
export const MUTATION_CREATE_BULK_MESSAGING_CAMPAIGN = gql`
  mutation CreateBulkMessagingCampaign($input: InputBulkMessagingCampaign) {
    createBulkMessagingCampaign(input: $input) {
      message
      data {
        id
        campaignName
        isDraft
        messageTemplate
        mediaCount
        campaignType
        a2pCampaignId
        a2pCampaignType
        a2pSampleId
        messagingType
        status
        scheduleType
        isScheduled
        initiatedAt
        scheduledAt
        maxOccurrence
        endAt
        attachments {
          id
          fileName
          mimetype
          fileSize
          fileUrl
        }
        metrics {
          key
          value
        }
        createdBy {
          id
          name
          profilePicture {
            id
            url
            originalname
            size
            key
            mimetype
            createdAt
          }
          roles {
            id
            isActive
            isDefault
            label
            position
            slug
            level
          }
        }
        createdAt
        deliverySummary {
          totalSent
          totalRecipients
          runStatus
        }
      }
    }
  }
`;

export const MUTATION_UPDATE_BULK_MESSAGING_CAMPAIGN = gql`
  mutation UpdateBulkMessagingCampaign($campaignId: Int!, $input: InputBulkMessagingCampaign) {
    updateBulkMessagingCampaign(campaignId: $campaignId, input: $input) {
      message
      data {
        id
        campaignName
        isDraft
        messageTemplate
        mediaCount
        campaignType
        messagingType
        a2pCampaignId
        a2pCampaignType
        a2pSampleId
        status
        scheduleType
        isScheduled
        initiatedAt
        scheduledAt
        maxOccurrence
        endAt
        attachments {
          id
          fileName
          mimetype
          fileSize
          fileUrl
        }
        metrics {
          key
          value
        }
        createdBy {
          id
          name
          profilePicture {
            id
            url
            originalname
            size
            key
            mimetype
            createdAt
          }
          roles {
            id
            label
            slug
            level
            isActive
            isDefault
            position
          }
        }
        createdAt
        deliverySummary {
          totalSent
          totalRecipients
          runStatus
        }
      }
    }
  }
`;
export const MUTATION_REMOVE_WORKSPACE_USER_WITH_DISPOSITION = gql`
  mutation RemoveWorkspaceUserWithDisposition($input: RemoveMemberDispositionInput!) {
    removeWorkspaceUserWithDisposition(input: $input) {
      message
    }
  }
`;

export const MUTATION_PURCHASE_PACKAGE = gql`
  mutation PurchasePackage($input: InputPurchasePackage!) {
    purchasePackage(input: $input) {
      message
      data {
        id
        packageId
        isActive
        status
        startedAt
        expiredAt
        autoRenew
        nextBillingAt
      }
    }
  }
`;
export const DELETE_BULK_MESSAGING_CAMPAIGN = gql`
  mutation DeleteBulkMessagingCampaign($campaignId: Int!) {
    deleteBulkMessagingCampaign(campaignId: $campaignId) {
      message
    }
  }
`;

export const DUPLICATE_BULK_MESSAGING_CAMPAIGN = gql`
  mutation DuplicateBulkMessagingCampaign($campaignId: Int!) {
    duplicateBulkMessagingCampaign(campaignId: $campaignId) {
      message
    }
  }
`;

export const MUTATION_PAUSE_RECURRING_BULK_MESSAGING_CAMPAIGN = gql`
  mutation PauseRecurringBulkMessagingCampaign($campaignId: Int!) {
    pauseRecurringBulkMessagingCampaign(campaignId: $campaignId) {
      message
    }
  }
`;

export const MUTATION_RESUME_RECURRING_BULK_MESSAGING_CAMPAIGN = gql`
  mutation ResumeRecurringBulkMessagingCampaign($campaignId: Int!) {
    resumeRecurringBulkMessagingCampaign(campaignId: $campaignId) {
      message
    }
  }
`;

export const MUTATION_TERMINATE_RECURRING_BULK_MESSAGING_CAMPAIGN = gql`
  mutation TerminateRecurringBulkMessagingCampaign($campaignId: Int!) {
    terminateRecurringBulkMessagingCampaign(campaignId: $campaignId) {
      message
    }
  }
`;

export const MUTATION_SET_CALL_SETTING_EMAIL_ALERTS = gql`
  mutation SetCallSettingEmailAlerts($setCallSettingEmailAlertsId: Int!, $input: InputEmailAlerts!) {
    setCallSettingEmailAlerts(id: $setCallSettingEmailAlertsId, input: $input) {
      message
      data {
        enableMissedCallEmail
        smsMmsEmailAlertEnabled
        transcriptionEmailAlertEnabled
      }
    }
  }
`;
export const MUTATION_SET_CHANNEL_SETTING_EMAIL_ALERTS = gql`
  mutation SetChannelSettingEmailAlerts($channelId: Int!, $input: InputEmailAlerts!) {
    setChannelSettingEmailAlerts(channelId: $channelId, input: $input) {
      message
      data {
        enableMissedCallEmail
        smsMmsEmailAlertEnabled
        transcriptionEmailAlertEnabled
      }
    }
  }
`;

export const MUTATION_SET_AUTO_RECHARGE_CONFIG_V2 = gql`
  mutation SetAutoRechargeConfigV2($input: InputAutoRechargeConfig) {
    setAutoRechargeConfigV2(input: $input) {
      message
      data {
        enabled
        creditThreshold
        credit
        targetBalance
        isDisableAllowed
      }
    }
  }
`;

export const MUTATION_RELEASE_CHANNELS = gql`
  mutation ReleaseChannels($input: InputReleaseChannels) {
    releaseChannels(input: $input) {
      message
    }
  }
`;

export const MUTATION_PURCHASE_NUMBERS = gql`
  mutation PurchaseNumbers($input: InputPurchaseNumbers) {
    purchaseNumbers(input: $input) {
      message
      data {
        duplicates
      }
    }
  }
`;

export const MUTATION_CONFIRM_PAYMENT_ORDER = gql`
  mutation ConfirmPaymentOrder($paymentOrderId: Int!) {
    confirmPaymentOrder(paymentOrderId: $paymentOrderId) {
      message
      data {
        id
        status
        lienStatus
      }
    }
  }
`;

export const MUTATION_REJECT_PAYMENT_ORDER = gql`
  mutation RejectPaymentOrder($input: InputRejectPaymentOrder!) {
    rejectPaymentOrder(input: $input) {
      message
      data {
        id
        status
        lienStatus
      }
    }
  }
`;

export const MUTATION_REMOVE_CHANNEL_REMOVAL_SCHEDULE = gql`
  mutation RemoveChannelRemovalSchedule($channelId: Int!) {
    removeChannelRemovalSchedule(channelId: $channelId) {
      message
    }
  }
`;

export const MUTATION_EXPORT_SUBSCRIPTION_OVERVIEW = gql`
  mutation ExportSubscriptionOverview($include: [SubscriptionOverviewIncludeEnum!]) {
    exportSubscriptionOverview(include: $include) {
      message
    }
  }
`;

export const MUTATION_DELETE_UNUSED_CONTACTS = gql`
  mutation DeleteUnusedContacts($input: InputDeleteUnusedContacts!) {
    deleteUnusedContacts(input: $input) {
      message
    }
  }
`;

export const MUTATION_CREATE_PAYMENT_INTENT_V3 = gql`
  mutation CreatePaymentIntentV3($input: InputPaymentIntentV3) {
    createPaymentIntentV3(input: $input) {
      message
      data
    }
  }
`;

export const MUTATION_HOLD_CALL = gql`
  mutation HoldCall($callSid: String!, $input: InputHoldCall!) {
    holdCall(callSid: $callSid, input: $input) {
      message
    }
  }
`;
export const MUTATION_READ_ALL_MESSAGES_FOR_CHANNEL = gql`
  mutation ReadAllMessagesForChannel($channelId: Int!) {
    readAllMessagesForChannel(channelId: $channelId) {
      message
    }
  }
`;

export const MUTATION_CREATE_AI_AGENT = gql`
  ${AI_AGENT_FIELDS}
  mutation CreateAiAgent($input: CreateAiAgentInput!) {
    createAiAgent(input: $input) {
      message
      data {
        ...AiAgentFields
      }
    }
  }
`;

export const MUTATION_UPDATE_AI_AGENT = gql`
  ${AI_AGENT_FIELDS}
  mutation UpdateAiAgent($id: Int!, $input: UpdateAiAgentInput!) {
    updateAiAgent(id: $id, input: $input) {
      message
      data {
        ...AiAgentFields
      }
    }
  }
`;

export const MUTATION_SET_AI_AGENT_NUMBERS = gql`
  ${AI_AGENT_FIELDS}
  mutation SetAiAgentNumbers($id: Int!, $channelIds: [Int!]!, $replacePrevious: Boolean) {
    setAiAgentNumbers(id: $id, channelIds: $channelIds, replacePrevious: $replacePrevious) {
      message
      data {
        ...AiAgentFields
      }
    }
  }
`;

export const MUTATION_DELETE_AI_AGENT = gql`
  ${AI_AGENT_FIELDS}
  mutation DeleteAiAgent($id: Int!) {
    deleteAiAgent(id: $id) {
      message
      data {
        ...AiAgentFields
      }
    }
  }
`;

export const MUTATION_CREATE_AI_AGENT_TEST_CALL_TOKEN = gql`
  mutation CreateAiAgentTestCallToken($aiAgentId: Int!) {
    createAiAgentTestCallToken(aiAgentId: $aiAgentId) {
      message
      data {
        token
      }
    }
  }
`;

export const MUTATION_CREATE_AI_KNOWLEDGE_SOURCE = gql`
  mutation CreateAiKnowledgeSource($input: InputCreateAiKnowledgeSource!) {
    createAiKnowledgeSource(input: $input) {
      message
      data {
        id
        name
        type
        itemCount
        promptText
        website {
          url
          depth
        }
        description
        assignedAgents {
          id
          name
        }
        createdAt
        updatedAt
      }
    }
  }
`;

export const MUTATION_UPDATE_AI_KNOWLEDGE_SOURCE = gql`
  mutation UpdateAiKnowledgeSource($updateAiKnowledgeSourceId: Int!, $input: InputUpdateAiKnowledgeSource!) {
    updateAiKnowledgeSource(id: $updateAiKnowledgeSourceId, input: $input) {
      message
      data {
        id
        name
        type
        itemCount
        promptText
        website {
          url
          depth
        }
        description
        assignedAgents {
          id
          name
        }
        createdAt
        updatedAt
      }
    }
  }
`;

export const MUTATION_DELETE_AI_KNOWLEDGE_SOURCE = gql`
  mutation DeleteAiKnowledgeSource($deleteAiKnowledgeSourceId: Int!) {
    deleteAiKnowledgeSource(id: $deleteAiKnowledgeSourceId) {
      message
    }
  }
`;

export const MUTATION_EXPORT_BULK_IMPORT_JOB_ROWS = gql`
  mutation ExportBulkImportJobRows($jobId: String!, $query: String, $status: BulkImportRowStatusEnum) {
    exportBulkImportJobRows(jobId: $jobId, query: $query, status: $status) {
      message
    }
  }
`;

export const MUTATION_CONFIRM_WALLET_DEDUCTION_REQUEST = gql`
  mutation ConfirmWalletDeduction {
    confirmWalletDeduction {
      message
      data {
        status
        amount
        lineItemLabel
        deductedAt
      }
    }
  }
`;

export const MUTATION_FORWARD_MESSAGE = gql`
  mutation ForwardMessage($input: InputForwardMessage!) {
    forwardMessage(input: $input) {
      message
      data {
        roomId
        roomIdentity
        chatMessageId
      }
    }
  }
`;

export const MUTATION_SCHEDULE_CHAT_MESSAGE = gql`
  mutation scheduleChatMessage($input: InputChatMessageSchedule!) {
    scheduleChatMessage(input: $input) {
      message
      data {
        ...ChatMessageScheduleFields
      }
    }
  }
  ${CHAT_MESSAGE_SCHEDULE_FIELDS}
`;

export const MUTATION_UPDATE_SCHEDULED_CHAT_MESSAGE = gql`
  mutation updateScheduledChatMessage($id: String!, $input: InputUpdateChatMessageSchedule!) {
    updateScheduledChatMessage(id: $id, input: $input) {
      message
      data {
        ...ChatMessageScheduleFields
      }
    }
  }
  ${CHAT_MESSAGE_SCHEDULE_FIELDS}
`;

export const MUTATION_CANCEL_SCHEDULED_CHAT_MESSAGE = gql`
  mutation cancelScheduledChatMessage($id: String!) {
    cancelScheduledChatMessage(id: $id) {
      message
      data
    }
  }
`;

export const MUTATION_DELETE_SCHEDULED_CHAT_MESSAGE = gql`
  mutation deleteScheduledChatMessage($id: String!) {
    deleteScheduledChatMessage(id: $id) {
      message
      data
    }
  }
`;

export const MUTATION_SEND_SCHEDULED_CHAT_MESSAGE_NOW = gql`
  mutation sendScheduledChatMessageNow($id: String!) {
    sendScheduledChatMessageNow(id: $id) {
      message
      data
    }
  }
`;

export const MUTATION_MARK_CHAT_MESSAGES_DELIVERED = gql`
  mutation markChatMessagesDelivered($roomId: Int!, $upToChatMessageId: Int!) {
    markChatMessagesDelivered(roomId: $roomId, upToChatMessageId: $upToChatMessageId) {
      message
      data
    }
  }
`;

export const MUTATION_MARK_CHAT_MESSAGES_READ = gql`
  mutation markChatMessagesRead($roomId: Int!, $upToChatMessageId: Int!) {
    markChatMessagesRead(roomId: $roomId, upToChatMessageId: $upToChatMessageId) {
      message
      data
    }
  }
`;

export const MUTATION_MARK_PENDING_CHAT_MESSAGES_DELIVERED = gql`
  mutation markPendingChatMessagesDelivered {
    markPendingChatMessagesDelivered {
      message
      data
    }
  }
`;

export const MUTATION_REACT_CHAT_MESSAGE = gql`
  mutation reactChatMessage($chatMessageId: Int!, $emoji: String!) {
    reactChatMessage(chatMessageId: $chatMessageId, emoji: $emoji) {
      message
      data
    }
  }
`;

export const MUTATION_UNREACT_CHAT_MESSAGE = gql`
  mutation unreactChatMessage($chatMessageId: Int!, $emoji: String!) {
    unreactChatMessage(chatMessageId: $chatMessageId, emoji: $emoji) {
      message
      data
    }
  }
`;

export const CREATE_CHAT_ROOM_NOTE = gql`
  mutation CreateChatRoomNote($input: InputChatRoomNote!) {
    createChatRoomNote(input: $input) {
      message
      data {
        ...ChatRoomNoteFields
      }
    }
  }
  ${CHAT_ROOM_NOTE_FIELDS}
`;

export const UPDATE_CHAT_ROOM_NOTE = gql`
  mutation UpdateChatRoomNote($id: Int!, $input: InputUpdateChatRoomNote!) {
    updateChatRoomNote(id: $id, input: $input) {
      message
      data {
        ...ChatRoomNoteFields
      }
    }
  }
  ${CHAT_ROOM_NOTE_FIELDS}
`;

export const DELETE_CHAT_ROOM_NOTE = gql`
  mutation DeleteChatRoomNote($id: Int!) {
    deleteChatRoomNote(id: $id) {
      message
      data {
        id
      }
    }
  }
`;

export const MUTATION_SET_CALL_QUEUE_IN_CHANNEL_SETTING = gql`
  mutation SetCallQueueInChannelSetting($channelId: Int!, $input: InputCallQueue!) {
    setCallQueueInChannelSetting(channelId: $channelId, input: $input) {
      message
      data {
        id
      }
    }
  }
`;

export const MUTATION_SET_CALL_QUEUE_IN_CALL_SETTING = gql`
  mutation SetCallQueueInCallSetting($callSettingId: Int!, $input: InputCallQueue!) {
    setCallQueueInCallSetting(callSettingId: $callSettingId, input: $input) {
      message
      data {
        id
      }
    }
  }
`;

export const MUTATION_PICK_NEXT_QUEUED_CALL = gql`
  mutation PickNextQueuedCall($callQueueId: String!) {
    pickNextQueuedCall(callQueueId: $callQueueId) {
      message
      data {
        friendlyName
        messageConversationId
      }
    }
  }
`;

export const MUTATION_SAVE_AI_AGENT_CONVERSATION_MODE = gql`
  mutation SaveAiAgentConversationMode($aiAgentId: Int!, $input: InputAiAgentConversationMode!) {
    saveAiAgentConversationMode(aiAgentId: $aiAgentId, input: $input) {
      message
      data {
        mode
        effectiveMode
        options {
          mode
          unavailableReason
        }
        destination {
          type
          phoneNumber
          memberIds
          teamId
        }
        screenAndTransfer {
          escalationTrigger
          transferMessage
          intakeQuestions
          fallbackRoute
        }
        hybridParallel {
          emergencyCover {
            enabled
            expiresAt
            isActive
          }
          overflowWhenBusy
          noAnswerEnabled
          noAnswerSeconds
          savedContactsToHuman
          returningCallersEnabled
          returningCallersWithinDays
        }
      }
    }
  }
`;

export const MUTATION_SET_AI_AGENT_END_CALL = gql`
  mutation SetAiAgentEndCall($aiAgentId: Int!, $input: InputAiAgentEndCall!) {
    setAiAgentEndCall(aiAgentId: $aiAgentId, input: $input) {
      message
      data {
        isActive
        unavailableReason
        endTrigger
        confirmBeforeEnding
        endCallGuidance
      }
    }
  }
`;

export const MUTATION_SET_AI_AGENT_FORWARD_TO_MEMBERS = gql`
  mutation SetAiAgentForwardToMembers($aiAgentId: Int!, $input: InputAiAgentForwardToMembers!) {
    setAiAgentForwardToMembers(aiAgentId: $aiAgentId, input: $input) {
      message
      data {
        isActive
        unavailableReason
        mode
        memberIds
      }
    }
  }
`;
